import React, { useEffect, useState } from "react";
import rechargeAPI, {
  Recharge,
  CreateRechargeData,
} from "../../services/rechargeAPI";
import { nfcCardAPI } from "../../services/nfcCardAPI";
import { branchAPI } from "../../services/branchAPI";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Tooltip,
  IconButton,
  alpha,
} from "@mui/material";
import {
  Add as AddIcon,
  Check as CheckIcon,
  Payment as PaymentIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
} from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { useRBAC } from "../../hooks/useRBAC";
import PageHeader from "../../components/layout/PageHeader";
import DataListCard from "../../components/layout/DataListCard";
import SortableTableCell from "../../components/layout/SortableTableCell";
import TableActionButton from "../../components/layout/TableActionButton";
import { useTableSort } from "../../hooks/useTableSort";
import { tokens } from "../../theme";
import { BRANCHES_ALL_QUERY_KEY } from "../../utils/pagination";

type RechargeSortField =
  | "holder"
  | "amount"
  | "paymentMethod"
  | "branch"
  | "status"
  | "createdAt";

const rechargeSchema = yup.object({
  nfcCardId: yup.string().required("NFC card is required"),
  amount: yup
    .number()
    .positive("Amount must be positive")
    .required("Amount is required"),
  paymentMethod: yup.string().required("Payment method is required"),
  notes: yup.string().optional(),
});

type RechargeFormData = yup.InferType<typeof rechargeSchema>;
type StatusFilter = "all" | Recharge["status"];
type PaymentFilter = "all" | "cash" | "card" | "upi" | "netbanking";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

const paymentLabel = (method: string) => {
  const labels: Record<string, string> = {
    cash: "Cash",
    card: "Card",
    upi: "UPI",
    netbanking: "Net Banking",
  };
  return labels[method] || method;
};

const RechargesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { canCreate, canUpdate } = useRBAC();
  const { sortBy, sortOrder, handleSort, sortParams } =
    useTableSort<RechargeSortField>("createdAt", "desc");
  const onSort = (field: RechargeSortField) => handleSort(field, () => setPage(0));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [paymentFilter, setPaymentFilter] = useState<PaymentFilter>("all");
  const [branchFilter, setBranchFilter] = useState<string>("all");
  const [openDialog, setOpenDialog] = useState(false);
  const [alert, setAlert] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: nfcCardsData } = useQuery({
    queryKey: ["nfcCardsForRecharge"],
    queryFn: () => nfcCardAPI.getCards({ page: 1, limit: 200 }),
  });

  const { data: branches = [] } = useQuery({
    queryKey: BRANCHES_ALL_QUERY_KEY,
    queryFn: () => branchAPI.getAllBranches(),
  });

  const {
    data: rechargesData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      "recharges",
      page,
      rowsPerPage,
      debouncedSearch,
      statusFilter,
      paymentFilter,
      branchFilter,
      sortBy,
      sortOrder,
    ],
    queryFn: () =>
      rechargeAPI.getRecharges({
        pageSize: rowsPerPage,
        pageNo: page + 1,
        ...sortParams,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(statusFilter !== "all" ? { status: statusFilter } : {}),
        ...(paymentFilter !== "all" ? { paymentMethod: paymentFilter } : {}),
        ...(branchFilter !== "all" ? { branchId: branchFilter } : {}),
      }),
  });

  const { mutate: createRecharge, isPending: isCreating } = useMutation({
    mutationFn: (data: CreateRechargeData) => rechargeAPI.createRecharge(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recharges"] });
      queryClient.invalidateQueries({ queryKey: ["transactions"] });
      queryClient.invalidateQueries({ queryKey: ["dashboardRecentTx"] });
      queryClient.invalidateQueries({ queryKey: ["transactionStats"] });
      setAlert({ type: "success", message: "Recharge created successfully" });
      handleCloseDialog();
    },
    onError: (err: any) => {
      setAlert({
        type: "error",
        message: err.response?.data?.message || "Failed to create recharge",
      });
    },
  });

  const { mutate: completeRecharge } = useMutation({
    mutationFn: (id: string) => rechargeAPI.completeRecharge(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recharges"] });
      queryClient.invalidateQueries({ queryKey: ["transactions"] });
      queryClient.invalidateQueries({ queryKey: ["dashboardRecentTx"] });
      queryClient.invalidateQueries({ queryKey: ["transactionStats"] });
      setAlert({ type: "success", message: "Recharge completed successfully" });
    },
    onError: (err: any) => {
      setAlert({
        type: "error",
        message: err.response?.data?.message || "Failed to complete recharge",
      });
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<RechargeFormData>({
    resolver: yupResolver(rechargeSchema),
    defaultValues: {
      nfcCardId: "",
      amount: 0,
      paymentMethod: "cash",
      notes: "",
    },
  });

  const recharges = rechargesData?.data || [];
  const totalCount = rechargesData?.total || 0;

  const handleOpenDialog = () => {
    reset({
      nfcCardId: "",
      amount: 0,
      paymentMethod: "cash",
      notes: "",
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    reset();
  };

  const onSubmit = (data: RechargeFormData) => {
    createRecharge({
      nfcCardId: data.nfcCardId,
      amount: data.amount,
      paymentMethod: data.paymentMethod,
      notes: data.notes,
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "success";
      case "pending":
        return "warning";
      case "failed":
        return "error";
      default:
        return "default";
    }
  };

  const filterDropdowns = (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      flexWrap="wrap"
      sx={{ width: "100%" }}
    >
      <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 160 } }}>
        <InputLabel>Branch</InputLabel>
        <Select
          value={branchFilter}
          label="Branch"
          onChange={(e) => {
            setBranchFilter(e.target.value);
            setPage(0);
          }}
        >
          <MenuItem value="all">All Branches</MenuItem>
          {branches.map((branch) => (
            <MenuItem key={branch.id} value={branch.id}>
              {branch.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 140 } }}>
        <InputLabel>Status</InputLabel>
        <Select
          value={statusFilter}
          label="Status"
          onChange={(e) => {
            setStatusFilter(e.target.value as StatusFilter);
            setPage(0);
          }}
        >
          <MenuItem value="all">All Status</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="completed">Completed</MenuItem>
          <MenuItem value="failed">Failed</MenuItem>
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 160 } }}>
        <InputLabel>Payment</InputLabel>
        <Select
          value={paymentFilter}
          label="Payment"
          onChange={(e) => {
            setPaymentFilter(e.target.value as PaymentFilter);
            setPage(0);
          }}
        >
          <MenuItem value="all">All Methods</MenuItem>
          <MenuItem value="cash">Cash</MenuItem>
          <MenuItem value="card">Card</MenuItem>
          <MenuItem value="upi">UPI</MenuItem>
          <MenuItem value="netbanking">Net Banking</MenuItem>
        </Select>
      </FormControl>
    </Stack>
  );

  return (
    <Box>
      <PageHeader
        title="Recharges"
        subtitle="Manage card top-ups and recharge history"
        breadcrumbs={[
          { label: "Dashboard", to: "/dashboard" },
          { label: "Recharges" },
        ]}
        action={
          <>
            <Tooltip title="Export PDF">
              <IconButton size="small" sx={{ border: "1px solid", borderColor: "divider" }}>
                <PdfIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export Excel">
              <IconButton size="small" sx={{ border: "1px solid", borderColor: "divider" }}>
                <ExcelIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {canCreate("recharges") && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenDialog}>
                Create Recharge
              </Button>
            )}
          </>
        }
      />

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error instanceof Error ? error.message : "Failed to load recharges"}
        </Alert>
      )}

      <DataListCard
        searchPlaceholder="Search holder, phone, email…"
        searchValue={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(0);
        }}
        filters={filterDropdowns}
        footer={
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{ width: "100%" }}
          />
        }
      >
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <Typography color="text.secondary">Loading recharges…</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={72}>S.No.</TableCell>
                  <SortableTableCell label="Card / Holder" field="holder" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                  <SortableTableCell label="Amount" field="amount" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                  <SortableTableCell label="Payment" field="paymentMethod" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                  <SortableTableCell label="Branch" field="branch" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                  <SortableTableCell label="Status" field="status" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                  <SortableTableCell label="Date" field="createdAt" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recharges.map((recharge: Recharge, index: number) => (
                  <TableRow key={recharge.id} hover>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {page * rowsPerPage + index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box
                          sx={{
                            width: 38,
                            height: 38,
                            borderRadius: tokens.radius.sm,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: alpha(tokens.colors.dashOrange, 0.12),
                            color: tokens.colors.dashOrange,
                            flexShrink: 0,
                          }}
                        >
                          <PaymentIcon sx={{ fontSize: 20 }} />
                        </Box>
                        <Box minWidth={0}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {recharge.nfcCard?.displayNumber ||
                              recharge.cardHolder?.holderName ||
                              "—"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {recharge.cardHolder?.holderName
                              ? recharge.cardHolder.holderName
                              : recharge.cardHolder?.phone || "—"}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} color="primary.main">
                        {formatCurrency(recharge.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {paymentLabel(recharge.paymentMethod)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {recharge.branch?.name || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={recharge.status.toUpperCase()}
                        color={getStatusColor(recharge.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {recharge.createdAt
                          ? format(new Date(recharge.createdAt), "dd MMM yyyy, HH:mm")
                          : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {recharge.status === "pending" && canUpdate("recharges") && (
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <TableActionButton
                            title="Complete recharge"
                            variant="success"
                            onClick={() => completeRecharge(recharge.id)}
                          >
                            <CheckIcon sx={{ fontSize: 18 }} />
                          </TableActionButton>
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {recharges.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">No recharges found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DataListCard>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle sx={{ fontWeight: 700 }}>Create recharge</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ pt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="nfcCardId"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.nfcCardId}>
                      <InputLabel>NFC card</InputLabel>
                      <Select {...field} label="NFC card">
                        <MenuItem value="">
                          <em>Select NFC card</em>
                        </MenuItem>
                        {(nfcCardsData?.cards || []).map((card: any) => (
                          <MenuItem key={card.id} value={card.id}>
                            {card.displayNumber} — {formatCurrency(Number(card.balance))}
                            {card.holder?.holderName ? ` (${card.holder.holderName})` : ""}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.nfcCardId && (
                        <FormHelperText>{errors.nfcCardId.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="amount"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Amount (₹)"
                      type="number"
                      inputProps={{ step: "0.01", min: "0" }}
                      error={!!errors.amount}
                      helperText={errors.amount?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="paymentMethod"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.paymentMethod}>
                      <InputLabel>Payment method</InputLabel>
                      <Select {...field} label="Payment method">
                        <MenuItem value="cash">Cash</MenuItem>
                        <MenuItem value="card">Card</MenuItem>
                        <MenuItem value="upi">UPI</MenuItem>
                        <MenuItem value="netbanking">Net Banking</MenuItem>
                      </Select>
                      {errors.paymentMethod && (
                        <FormHelperText>{errors.paymentMethod.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="notes"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} fullWidth label="Notes" multiline rows={2} />
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="inherit">
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={isCreating}>
              {isCreating ? "Creating…" : "Create"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default RechargesPage;
